import { useEffect, useRef, type FC } from 'react';

import { NavigationTopBarAlert } from '@livechat/design-system-react-components';
import { useDispatch } from 'react-redux';

import { TopBarNotificationType } from 'constants/notifications';
import { ToastContent } from 'constants/toasts';
import { openInCard } from 'helpers/routing';
import { showToast } from 'helpers/toast';
import { useAgentCustomProperty } from 'hooks/use-agent-custom-property';
import { AgentCustomPropertyName } from 'store/features/agent-custom-properties/interfaces';
import { NotificationsBarActions } from 'store/features/notifications-bar/actions';
import { ToastVariant } from 'store/features/toasts/interfaces';

import { LEGACY_MIGRATION_COPY, LEGACY_MIGRATION_DISMISS_DURATION_MS, LegacyMigrationEvent } from './constants';
import { getLegacyMigrationLandingUrl, trackLegacyMigrationEvent } from './helpers';

interface Props {
  isVisible: boolean;
}

export const LegacyMigrationBar: FC<Props> = ({ isVisible }) => {
  const dispatch = useDispatch();
  const wasVisible = useRef(false);
  const [, setDismissedUntil] = useAgentCustomProperty<number>(
    AgentCustomPropertyName.LegacyMigrationTopbarDismissedUntil,
    0,
  );
  const [, setPermanentlyDismissed] = useAgentCustomProperty<boolean>(
    AgentCustomPropertyName.LegacyMigrationTopbarPermanentlyDismissed,
    false,
  );

  useEffect(() => {
    if (isVisible && !wasVisible.current) {
      trackLegacyMigrationEvent(LegacyMigrationEvent.TopbarDisplayed);
    }

    wasVisible.current = isVisible;
  }, [isVisible]);

  const hideTopbar = (): void => {
    dispatch(NotificationsBarActions.hideNotificationsBar(TopBarNotificationType.LegacyMigration));
  };

  const handleClose = (): void => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.TopbarDismissed);
    setDismissedUntil(Date.now() + LEGACY_MIGRATION_DISMISS_DURATION_MS);
    hideTopbar();
  };

  const handlePrimaryAction = (): void => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.TopbarCtaClicked);
    setPermanentlyDismissed(true, {
      onSuccess: () => {
        hideTopbar();
        openInCard(getLegacyMigrationLandingUrl());
      },
      onFailure: () => showToast(ToastVariant.Error, ToastContent.AGENT_CUSTOM_PROPERTY_UPDATE_FAILURE),
    });
  };

  return (
    <NavigationTopBarAlert
      kind="info"
      isVisible={isVisible}
      primaryCta={{
        label: LEGACY_MIGRATION_COPY.primaryAction,
        onClick: handlePrimaryAction,
        size: 'compact',
      }}
      closeButton={{
        onClick: handleClose,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'aria-label': 'Close the Text migration notification',
      }}
    >
      {LEGACY_MIGRATION_COPY.topbarMessage}
    </NavigationTopBarAlert>
  );
};
